import { hoyLocal, fechaLocal } from '@/lib/fechas';
import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { UserCog, Plus, Trash2, Eye, EyeOff, Edit, Crown, ShieldCheck, Wallet, Sparkles, Wrench } from 'lucide-react';

const PUESTOS_LOGIN = [
  { value: 'gerente', label: 'Gerente', desc: 'Casi todos los permisos (excepto config y suscripcion)', Icon: ShieldCheck, color: 'text-amber-400' },
  { value: 'cajero', label: 'Cajero', desc: 'Cobra, agenda citas, hace corte de caja, descuentos limitados', Icon: Wallet, color: 'text-emerald-400' },
  { value: 'operario', label: 'Operario', desc: 'Solo ve y mueve turnos del dia. Sin acceso a precios ni cobros.', Icon: Sparkles, color: 'text-cyan-400' },
];

const PUESTOS_NOMINA = [
  { value: 'lavador', label: 'Lavador' },
  { value: 'encargado', label: 'Encargado' },
  { value: 'supervisor', label: 'Supervisor' },
  { value: 'detallador', label: 'Detallador' },
  { value: 'recepcionista', label: 'Recepcionista' },
  { value: 'otro', label: 'Otro' },
];

const FORM_INICIAL = {
  nombre: '', email: '', password: '', telefono: '',
  puesto: 'lavador', tiene_acceso: false, salario_base: 0,
  tipo_pago: 'sueldo', frecuencia_pago: 'semanal',
  porcentaje_comision: 0, monto_por_carro: 0,
};

const puestoLabel = (p) => {
  const l = PUESTOS_LOGIN.find(x => x.value === p) || PUESTOS_NOMINA.find(x => x.value === p);
  return l?.label || p;
};
const puestoIcon = (p) => PUESTOS_LOGIN.find(x => x.value === p)?.Icon || Wrench;
const puestoColor = (p) => PUESTOS_LOGIN.find(x => x.value === p)?.color || 'text-slate-400';

export default function CarwashStaff() {
  const [staff, setStaff] = useState([]);
  const [dialog, setDialog] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState(FORM_INICIAL);
  const [cargando, setCargando] = useState(false);
  const [mostrarPwd, setMostrarPwd] = useState(false);

  const cargar = useCallback(async () => {
    try {
      const res = await api.get('/carwash/staff');
      setStaff(res.data);
    } catch { toast.error('Error al cargar staff'); }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const abrirNuevo = () => {
    setEditando(null);
    setForm(FORM_INICIAL);
    setDialog(true);
  };

  const abrirEditar = (s) => {
    setEditando(s);
    setForm({
      nombre: s.nombre || '',
      email: s.email || '',
      password: '',
      telefono: s.telefono || '',
      puesto: s.puesto || 'lavador',
      tiene_acceso: s.tiene_acceso || false,
      salario_base: s.salario_base || 0,
      tipo_pago: s.tipo_pago || 'sueldo',
      frecuencia_pago: s.frecuencia_pago || 'semanal',
      porcentaje_comision: s.porcentaje_comision || 0,
      monto_por_carro: s.monto_por_carro || 0,
    });
    setDialog(true);
  };

  const guardar = async () => {
    if (!form.nombre.trim()) { toast.error('Nombre obligatorio'); return; }
    if (form.tiene_acceso) {
      if (!editando && (!form.email.trim() || !form.password.trim())) {
        toast.error('Email y contrasena son obligatorios para acceso al sistema');
        return;
      }
      if (!['gerente', 'cajero', 'operario'].includes(form.puesto)) {
        toast.error('Selecciona un puesto valido (Gerente, Cajero u Operario)');
        return;
      }
    }
    setCargando(true);
    try {
      if (editando) {
        const payload = {
          nombre: form.nombre, telefono: form.telefono, salario_base: form.salario_base,
          puesto: form.puesto,
          tipo_pago: form.tipo_pago,
          frecuencia_pago: form.frecuencia_pago,
          porcentaje_comision: form.porcentaje_comision,
          monto_por_carro: form.monto_por_carro,
        };
        if (form.password) payload.password = form.password;
        await api.put(`/carwash/staff/${editando.id}`, payload);
        toast.success('Staff actualizado');
      } else {
        await api.post('/carwash/staff', form);
        toast.success('Staff creado');
      }
      setDialog(false);
      cargar();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al guardar');
    } finally { setCargando(false); }
  };

  const eliminar = async (id) => {
    if (!window.confirm('Eliminar este miembro del staff?')) return;
    try {
      await api.delete(`/carwash/staff/${id}`);
      toast.success('Staff eliminado');
      cargar();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al eliminar'); }
  };

  const toggleActivo = async (s) => {
    try {
      await api.put(`/carwash/staff/${s.id}`, { activo: !s.activo });
      cargar();
    } catch { toast.error('Error al actualizar'); }
  };

  const conLogin = staff.filter(s => s.tiene_acceso);
  const sinLogin = staff.filter(s => !s.tiene_acceso);

  const renderTarjeta = (s) => {
    const Icon = puestoIcon(s.puesto);
    return (
      <Card key={s.id} className="bg-slate-900/40 border-slate-800" data-testid={`staff-card-${s.id}`}>
        <CardContent className="p-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className={`w-9 h-9 rounded-sm bg-slate-800/60 flex items-center justify-center ${puestoColor(s.puesto)}`}>
              <Icon className="w-4 h-4" strokeWidth={1.5} />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-bold text-slate-200 truncate">{s.nombre}</p>
              <div className="flex items-center gap-2 flex-wrap">
                <Badge className={`text-[10px] ${s.tiene_acceso ? 'bg-cyan-500/20 text-cyan-400 border-cyan-700' : 'bg-slate-700/40 text-slate-400 border-slate-700'}`}>
                  {puestoLabel(s.puesto)}
                </Badge>
                {s.email && <span className="text-[11px] text-slate-500 truncate">{s.email}</span>}
                {!s.email && s.telefono && <span className="text-[11px] text-slate-500 truncate">{s.telefono}</span>}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <Badge className={s.activo ? 'bg-emerald-500/20 text-emerald-400 border-emerald-700 cursor-pointer' : 'bg-red-500/20 text-red-400 border-red-700 cursor-pointer'}
              onClick={() => toggleActivo(s)}
              data-testid={`staff-toggle-${s.id}`}>
              {s.activo ? 'Activo' : 'Inactivo'}
            </Badge>
            <Button variant="ghost" size="sm" onClick={() => abrirEditar(s)}
              className="text-slate-400 hover:text-slate-200" data-testid={`staff-editar-${s.id}`}>
              <Edit className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={() => eliminar(s.id)}
              className="text-slate-400 hover:text-red-400" data-testid={`staff-eliminar-${s.id}`}>
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-5" data-testid="carwash-staff-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-slate-100 font-barlow flex items-center gap-2">
            <UserCog className="w-5 h-5 text-cyan-400" /> Staff del Negocio
          </h2>
          <p className="text-xs text-slate-500 mt-1">
            Da de alta a tus empleados. Define su puesto y si tendran acceso al sistema.
          </p>
        </div>
        <Button onClick={abrirNuevo}
          className="bg-cyan-500 hover:bg-cyan-600 text-black font-bold rounded-sm" data-testid="nuevo-staff-btn">
          <Plus className="w-4 h-4 mr-1" /> Nuevo Staff
        </Button>
      </div>

      {/* Con acceso al sistema */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-cyan-400" />
          <h3 className="text-sm font-bold text-slate-200 uppercase tracking-wider">Con acceso al sistema ({conLogin.length})</h3>
        </div>
        {conLogin.length === 0 ? (
          <Card className="bg-slate-900/40 border-slate-800">
            <CardContent className="p-6 text-center">
              <p className="text-xs text-slate-500">No hay personal con acceso al sistema.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-2">{conLogin.map(renderTarjeta)}</div>
        )}
      </div>

      {/* Solo nomina */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Wrench className="w-4 h-4 text-slate-400" />
          <h3 className="text-sm font-bold text-slate-200 uppercase tracking-wider">Solo Nomina ({sinLogin.length})</h3>
        </div>
        {sinLogin.length === 0 ? (
          <Card className="bg-slate-900/40 border-slate-800">
            <CardContent className="p-6 text-center">
              <p className="text-xs text-slate-500">No hay empleados solo de nomina.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-2">{sinLogin.map(renderTarjeta)}</div>
        )}
      </div>

      {/* Produccion por operario */}
      <ProduccionOperarios />

      {/* Dialog */}
      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-slate-100 font-barlow">{editando ? 'Editar Staff' : 'Nuevo Staff'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-slate-800/40 rounded-sm">
              <div>
                <p className="text-sm text-slate-200 font-medium">Acceso al sistema</p>
                <p className="text-[11px] text-slate-500">Si esta activo, podra iniciar sesion con email y contrasena</p>
              </div>
              <Switch checked={form.tiene_acceso}
                disabled={!!editando}
                onCheckedChange={(v) => setForm({ ...form, tiene_acceso: v, puesto: v ? 'cajero' : 'lavador' })}
                data-testid="staff-acceso-switch" />
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-400">Nombre *</Label>
              <Input value={form.nombre} onChange={e => setForm({ ...form, nombre: e.target.value })}
                className="bg-slate-950 border-slate-700 text-slate-200 mt-1" data-testid="staff-nombre-input" />
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-400">Telefono</Label>
              <Input value={form.telefono} onChange={e => setForm({ ...form, telefono: e.target.value })}
                className="bg-slate-950 border-slate-700 text-slate-200 mt-1" data-testid="staff-telefono-input" />
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-400">Puesto *</Label>
              <Select value={form.puesto} onValueChange={v => setForm({ ...form, puesto: v })}>
                <SelectTrigger className="bg-slate-950 border-slate-700 text-slate-200 mt-1" data-testid="staff-puesto-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-800">
                  {form.tiene_acceso ? (
                    PUESTOS_LOGIN.map(p => (
                      <SelectItem key={p.value} value={p.value} className="text-slate-300" data-testid={`puesto-opt-${p.value}`}>
                        {p.label}
                      </SelectItem>
                    ))
                  ) : (
                    PUESTOS_NOMINA.map(p => (
                      <SelectItem key={p.value} value={p.value} className="text-slate-300">{p.label}</SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              {form.tiene_acceso && (
                <p className="text-[11px] text-slate-500 mt-1">
                  {PUESTOS_LOGIN.find(p => p.value === form.puesto)?.desc}
                </p>
              )}
            </div>

            {form.tiene_acceso && (
              <>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-400">Email *</Label>
                  <Input type="email" disabled={!!editando} value={form.email}
                    onChange={e => setForm({ ...form, email: e.target.value })}
                    className="bg-slate-950 border-slate-700 text-slate-200 mt-1 disabled:opacity-50"
                    data-testid="staff-email-input" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-400">
                    {editando ? 'Nueva contrasena (vacio = no cambiar)' : 'Contrasena *'}
                  </Label>
                  <div className="relative mt-1">
                    <Input type={mostrarPwd ? 'text' : 'password'} value={form.password}
                      onChange={e => setForm({ ...form, password: e.target.value })}
                      className="bg-slate-950 border-slate-700 text-slate-200 pr-10"
                      data-testid="staff-password-input" />
                    <button type="button" onClick={() => setMostrarPwd(!mostrarPwd)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500">
                      {mostrarPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              </>
            )}

            <div className="grid grid-cols-3 gap-2">
              <div className="col-span-2">
                <Label className="text-xs uppercase tracking-wider text-slate-400">
                  Sueldo por {form.frecuencia_pago === 'diario' ? 'día' : form.frecuencia_pago === 'quincenal' ? 'quincena' : 'semana'} (MXN)
                </Label>
                <Input type="number" min="0" step="50" value={form.salario_base}
                  onChange={e => setForm({ ...form, salario_base: parseFloat(e.target.value) || 0 })}
                  className="bg-slate-950 border-slate-700 text-slate-200 mt-1"
                  data-testid="staff-salario-input" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-400">Frecuencia</Label>
                <select value={form.frecuencia_pago}
                  onChange={e => setForm({ ...form, frecuencia_pago: e.target.value })}
                  className="w-full bg-slate-950 border border-slate-700 text-slate-200 mt-1 rounded-md px-2 py-2 text-sm h-10"
                  data-testid="staff-frecuencia-pago">
                  <option value="diario">Diario</option>
                  <option value="semanal">Semanal</option>
                  <option value="quincenal">Quincenal</option>
                </select>
              </div>
            </div>
            <p className="text-[10px] text-cyan-400/80 -mt-2">
              Este es el monto que le pagas cada {form.frecuencia_pago === 'diario' ? 'día' : form.frecuencia_pago === 'quincenal' ? 'quincena (15 días)' : 'semana (7 días)'}.
              Faltas descuentan sueldo diario = ${form.salario_base > 0 ? (form.salario_base / (form.frecuencia_pago === 'diario' ? 1 : form.frecuencia_pago === 'quincenal' ? 15 : 7)).toFixed(2) : '0.00'} por día.
            </p>

            {/* Tipo de pago: sueldo / comision / mixto */}
            <div className="border-t border-slate-800 pt-3">
              <Label className="text-xs uppercase tracking-wider text-slate-400 mb-2 block">Forma de pago</Label>
              <div className="grid grid-cols-3 gap-1.5 mb-2">
                {[
                  { v: 'sueldo', l: 'Sueldo fijo' },
                  { v: 'comision', l: 'Solo comisión' },
                  { v: 'mixto', l: 'Sueldo + comisión' },
                ].map(op => (
                  <button key={op.v} type="button"
                    onClick={() => setForm({ ...form, tipo_pago: op.v })}
                    className={`px-2 py-2 rounded-sm text-[11px] font-bold border transition-colors ${
                      form.tipo_pago === op.v
                        ? 'bg-cyan-500 border-cyan-500 text-black'
                        : 'bg-slate-950 border-slate-700 text-slate-400 hover:text-slate-200'
                    }`}
                    data-testid={`tipo-pago-${op.v}`}>
                    {op.l}
                  </button>
                ))}
              </div>
              {(form.tipo_pago === 'comision' || form.tipo_pago === 'mixto') && (
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider text-slate-500">% comisión (0-100)</Label>
                    <Input type="number" min="0" max="100" step="1" value={form.porcentaje_comision}
                      onChange={e => setForm({ ...form, porcentaje_comision: Math.max(0, Math.min(100, parseFloat(e.target.value) || 0)) })}
                      className="bg-slate-950 border-slate-700 text-slate-200 font-mono mt-1"
                      data-testid="staff-pct-comision" />
                    <p className="text-[9px] text-slate-500 mt-0.5">Sobre precio del turno</p>
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider text-slate-500">$ por carro</Label>
                    <Input type="number" min="0" step="1" value={form.monto_por_carro}
                      onChange={e => setForm({ ...form, monto_por_carro: Math.max(0, parseFloat(e.target.value) || 0) })}
                      className="bg-slate-950 border-slate-700 text-slate-200 font-mono mt-1"
                      data-testid="staff-monto-carro" />
                    <p className="text-[9px] text-slate-500 mt-0.5">Monto fijo por lavado</p>
                  </div>
                </div>
              )}
              <p className="text-[10px] text-slate-500 mt-2">
                {form.tipo_pago === 'sueldo' && `Recibirá $${form.salario_base.toLocaleString()} cada ${form.frecuencia_pago === 'diario' ? 'día' : form.frecuencia_pago === 'quincenal' ? 'quincena' : 'semana'} al pagar nómina.`}
                {form.tipo_pago === 'comision' && 'Recibirá % del precio + monto fijo por cada carro lavado en el periodo.'}
                {form.tipo_pago === 'mixto' && `Recibirá $${form.salario_base.toLocaleString()} ${form.frecuencia_pago === 'diario' ? 'diario' : form.frecuencia_pago === 'quincenal' ? 'quincenal' : 'semanal'} MÁS la comisión (% + $ por carro).`}
              </p>
            </div>

            <Button onClick={guardar} disabled={cargando}
              className="w-full bg-cyan-500 hover:bg-cyan-600 text-black font-bold rounded-sm"
              data-testid="guardar-staff-btn">
              {cargando ? 'Guardando...' : editando ? 'Actualizar' : 'Crear Staff'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function ProduccionOperarios() {
  const hoy = hoyLocal();
  const [desde, setDesde] = useState(hoy);
  const [hasta, setHasta] = useState(hoy);
  const [data, setData] = useState(null);
  const [cargando, setCargando] = useState(false);

  const consultar = useCallback(async () => {
    setCargando(true);
    try {
      const r = await api.get(`/carwash/produccion-operarios?desde=${desde}&hasta=${hasta}`);
      setData(r.data);
    } catch { toast.error('Error al cargar produccion'); }
    finally { setCargando(false); }
  }, [desde, hasta]);

  useEffect(() => { consultar(); }, [consultar]);

  const maxTurnos = Math.max(...((data?.operarios || []).map(o => o.turnos_totales)), 1);
  const fmt = (n) => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 }).format(n || 0);

  return (
    <Card className="bg-slate-900/40 border-slate-800 mt-6" data-testid="produccion-operarios-card">
      <CardContent className="p-5">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div>
            <h3 className="font-barlow text-lg text-slate-100 uppercase tracking-tight flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-cyan-400" /> Producción por operario
            </h3>
            <p className="text-[11px] text-slate-500">Turnos lavados e ingresos generados en el periodo</p>
          </div>
          <div className="flex items-center gap-2">
            <Input type="date" value={desde} onChange={e => setDesde(e.target.value)}
              className="bg-slate-950 border-slate-700 text-slate-200 text-xs h-8 w-36" data-testid="prod-desde" />
            <span className="text-slate-500 text-xs">→</span>
            <Input type="date" value={hasta} onChange={e => setHasta(e.target.value)}
              className="bg-slate-950 border-slate-700 text-slate-200 text-xs h-8 w-36" data-testid="prod-hasta" />
          </div>
        </div>

        {cargando ? (
          <p className="text-sm text-slate-500 text-center py-6">Cargando...</p>
        ) : (data?.operarios.length === 0 && !data?.sin_asignar) ? (
          <p className="text-sm text-slate-500 text-center py-6">Sin turnos asignados en este periodo.</p>
        ) : (
          <div className="space-y-1.5" data-testid="produccion-list">
            {data?.operarios.map((op, idx) => (
              <div key={op.operario_id} className="p-3 rounded-sm border border-slate-800 bg-slate-950/40" data-testid={`prod-row-${op.operario_id}`}>
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center gap-2 min-w-0">
                    <Badge className={`text-[10px] ${idx === 0 ? 'bg-amber-500/20 text-amber-400 border-amber-800' : 'bg-slate-800 text-slate-400 border-slate-700'}`}>#{idx + 1}</Badge>
                    <span className="text-sm text-slate-100 font-medium truncate">{op.operario_nombre}</span>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-sm font-mono text-cyan-400 font-bold">{op.turnos_totales} carros</p>
                    <p className="text-[11px] text-emerald-400 font-mono">{fmt(op.ingresos)}</p>
                  </div>
                </div>
                <div className="h-1 rounded-full bg-slate-800 overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-cyan-500 to-emerald-500" style={{ width: `${(op.turnos_totales / maxTurnos) * 100}%` }} />
                </div>
              </div>
            ))}
            {data?.sin_asignar && (
              <div className="p-3 rounded-sm border border-amber-800/40 bg-amber-500/5">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-amber-400 italic">Sin operario asignado</span>
                  <span className="text-sm font-mono text-amber-400 font-bold">{data.sin_asignar.turnos_totales} carros · {fmt(data.sin_asignar.ingresos)}</span>
                </div>
              </div>
            )}
            <div className="flex justify-between text-xs pt-2 border-t border-slate-800">
              <span className="text-slate-500">Total del periodo</span>
              <span className="text-slate-200 font-bold font-mono">{data?.total_turnos} carros · {fmt(data?.total_ingresos)}</span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
